import { useMemo } from 'react';
import {
  Button,
  Col,
  Row,
  Card,
  ButtonToolbar,
  ButtonGroup,
  Form,
} from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useNavigate, useParams } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import setFieldTouched from 'final-form-set-field-touched';
import { v4 as uuidv4 } from 'uuid';

import cloneDeep from 'lodash.clonedeep';
import get from 'lodash.get';

import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import { settingsSet } from '../store/settings_slice';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderError, renderOffline } from '../components/render_helpers';
import SubmitButtonSpinner from '../components/submit_button_spinner';
import Field from '../components/form/rff_field';
import InputField from '../components/form/input_field';
import { coerceInput, pickValues, handleSubmitError } from '../lib/utils';
import {
  siteLocationFormPageQuery,
  siteLocationCreate as siteLocationCreateMutation,
  siteLocationUpdate as siteLocationUpdateMutation,
} from '../graphql/site_location_queries';
import * as updateFunctions from '../update_functions';
import { siteLocationDefaultValues } from '../defaults';
import { siteLocationFormValidator } from '../validators';
import { siteLocationWhiteList } from '../white_lists';

const SiteLocationForm = () => {
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const [siteLocationCreate] = useMutation(siteLocationCreateMutation);
  const [siteLocationUpdate] = useMutation(siteLocationUpdateMutation);
  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    networkStatus: pageNetworkStatus,
  } = useQuery(siteLocationFormPageQuery, {
    variables: {
      hasSiteLocationId: !!params.id,
      siteLocationId: params.id || 0,
    },
    notifyOnNetworkStatusChange: true,
  });
  console.log(pageData);

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const initialValues = useMemo(() => {
    if (pageData?.siteLocation) {
      const values = pickValues(pageData.siteLocation, siteLocationWhiteList);
      return values;
    }
    return siteLocationDefaultValues;
  }, [pageData]);

  const onCancel = () => {
    navigate('/site_locations');
  };

  const onFormSubmit = async (data) => {
    const uuid = uuidv4();
    const submitData = cloneDeep(data);
    let mutation;
    let mutationMessageAction;
    const input = coerceInput(submitData);
    const mutationData = {
      variables: { input },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'SiteLocationType',
        recordId: params.id || uuid,
      },
    };
    if (params.id) {
      mutation = siteLocationUpdate;
      mutationMessageAction = 'update';
      mutationData.variables.id = params.id;
      mutationData.context.mutationType = 'UPDATE';
      mutationData.update = updateFunctions.siteLocationUpdate;
      mutationData.optimisticResponse = updateFunctions.optimisticNew({
        mutationName: 'siteLocationUpdate',
        mutationData,
        currentData: pageData.siteLocation,
      });
    } else {
      mutation = siteLocationCreate;
      mutationData.context.mutationType = 'CREATE';
      mutationMessageAction = 'create';
      mutationData.update = updateFunctions.siteLocationCreate;
      mutationData.optimisticResponse = updateFunctions.optimisticNew({
        mutationName: 'siteLocationCreate',
        mutationData,
      });
    }
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await mutation(mutationData);
        toastSuccess(`SiteLocation ${mutationMessageAction} succeeded`);
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
        navigate('/site_locations');
      } catch (err) {
        console.log(err);
        const { errorMessage, submitErrors } = handleSubmitError(err);
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
        toastError(errorMessage);
        return submitErrors;
      }
    } else {
      mutation(mutationData);
      toastWarning(
        `SiteLocation ${mutationMessageAction} ok locally. Go online to make permanent`
      );
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
      navigate('/site_locations');
    }
    return undefined;
  };

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm="auto">
          <h1 className="h3 mb-3">
            {params.id ? 'Edit Site Location' : 'New Site Location'}
          </h1>
        </Col>
        <Col>
          <Row className="justify-content-end g-0">
            <Col sm="auto">
              <LinkContainer to="/site_locations">
                <Button variant="primary">All Site Locations</Button>
              </LinkContainer>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col>
          <FinalForm
            initialValues={initialValues}
            onSubmit={(data) => onFormSubmit(data)}
            validate={siteLocationFormValidator}
            mutators={{ setFieldTouched }}
          >
            {({ handleSubmit, pristine, submitting }) => (
              <form noValidate>
                <Card>
                  <Card.Body>
                    <Field
                      name="name"
                      component={InputField}
                      labelWidth={4}
                      inputWidth={8}
                      size="lg"
                    >
                      Full Name
                    </Field>
                    <Field
                      name="siteLocationType"
                      component={InputField}
                      asElement="select"
                      selectOptions={Object.values(
                        get(pageData, 'enums.enums.SiteLocationTypes', [])
                      ).map((type) => ({
                        id: type,
                        name: type,
                      }))}
                      defaultSelectOptionText="select location type..."
                      labelWidth={4}
                      inputWidth={4}
                      size="lg"
                    >
                      Site Location Type
                    </Field>
                    <Field
                      name="siteId"
                      component={InputField}
                      asElement="select"
                      selectOptions={get(pageData, 'siteList', [])}
                      defaultSelectOptionText="select site..."
                      labelWidth={4}
                      inputWidth={4}
                      size="lg"
                    >
                      Site
                    </Field>
                    <Form.Group as={Row}>
                      <Col sm={12}>
                        <ButtonToolbar style={{ justifyContent: 'flex-end' }}>
                          <ButtonGroup className="me-2">
                            <Button
                              variant="danger"
                              onClick={onCancel}
                              disabled={submitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              type="button"
                              variant="primary"
                              disabled={pristine || submitting}
                              onClick={handleSubmit}
                            >
                              {submitting && <SubmitButtonSpinner />}
                              {params.id ? 'Update' : 'Create'}
                            </Button>
                          </ButtonGroup>
                        </ButtonToolbar>
                      </Col>
                    </Form.Group>
                  </Card.Body>
                </Card>
              </form>
            )}
          </FinalForm>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default SiteLocationForm;
